import React, { useState } from 'react';
import {
    Users,
    Clock,
    ArrowRight,
    Check,
    Mail,
    Share2,
    Globe,
    Building2,
    Calculator,
    PieChart,
    BarChart,
    LineChart
} from 'lucide-react';

const AssessmentWizard = ({ onNavigate }) => {
    const [step, setStep] = useState(0);
    const [assessment, setAssessment] = useState({
        dataSources: [],
        dataSize: '',
        processingNeeds: [],
        reportingNeeds: []
    });

    const getStorageOptions = () => {
        const baseOptions = [
            {
                icon: <Users className="w-5 h-5" />,
                label: "One Department",
                value: "small",
                subtext: "Managing data for a single team or department"
            },
            {
                icon: <Building2 className="w-5 h-5" />,
                label: "Multiple Departments",
                value: "medium",
                subtext: "Sharing data between several departments"
            },
            {
                icon: <Globe className="w-5 h-5" />,
                label: "Company-Wide",
                value: "large",
                subtext: "Organization-wide data management"
            }
        ];

        if (assessment.dataSources.includes('legacy') || assessment.dataSources.includes('external')) {
            baseOptions.push({
                icon: <Building2 className="w-5 h-5" />,
                label: "Multi-Company",
                value: "enterprise",
                subtext: "Managing data across multiple systems or companies"
            });
        }

        return baseOptions;
    };

    const questions = [
        {
            title: "Where are your data reserves hidden?",
            description: "Help us locate all the untapped sources of valuable business information",
            type: "multiSelect",
            field: "dataSources",
            options: [
                {
                    icon: <Users className="w-5 h-5" />,
                    label: "Team Members' Files",
                    value: "manual",
                    subtext: "Spreadsheets and documents on individual computers"
                },
                {
                    icon: <Mail className="w-5 h-5" />,
                    label: "Email & Attachments",
                    value: "email",
                    subtext: "Reports and updates shared via email"
                },
                {
                    icon: <Share2 className="w-5 h-5" />,
                    label: "Shared Folders",
                    value: "shared",
                    subtext: "Documents spread across network drives"
                },
                {
                    icon: <Globe className="w-5 h-5" />,
                    label: "Outside Partners",
                    value: "external",
                    subtext: "Information from customers, suppliers, or partners"
                },
                {
                    icon: <Building2 className="w-5 h-5" />,
                    label: "Older Systems",
                    value: "legacy",
                    subtext: "Data in existing business software"
                }
            ]
        },
        {
            title: "What's the scale of your data reservoir?",
            description: "Help us understand how much untapped potential we're working with",
            type: "singleSelect",
            field: "dataSize",
            options: getStorageOptions()
        },
        {
            title: "How should we refine your raw data?",
            description: "Select the ways you want to transform your data into valuable insights",
            type: "multiSelect",
            field: "processingNeeds",
            options: [
                {
                    icon: <Calculator className="w-5 h-5" />,
                    label: "Automatic Number Crunching",
                    value: "calculations",
                    subtext: "Let computers handle the complex math"
                },
                {
                    icon: <Clock className="w-5 h-5" />,
                    label: "Save Time on Reports",
                    value: "automation",
                    subtext: "Turn hours of work into minutes"
                },
                {
                    icon: <Share2 className="w-5 h-5" />,
                    label: "Combine Different Sources",
                    value: "consolidation",
                    subtext: "Bring all your information together"
                },
                {
                    icon: <LineChart className="w-5 h-5" />,
                    label: "Spot Trends",
                    value: "trends",
                    subtext: "Understand patterns in your business"
                }
            ]
        },
        {
            title: "How would you like your refined insights delivered?",
            description: "Choose how you want your valuable data products distributed",
            type: "multiSelect",
            field: "reportingNeeds",
            options: [
                {
                    icon: <PieChart className="w-5 h-5" />,
                    label: "Executive Summaries",
                    value: "executive",
                    subtext: "High-level overview of key metrics"
                },
                {
                    icon: <BarChart className="w-5 h-5" />,
                    label: "Department Reports",
                    value: "department",
                    subtext: "Detailed views for each team"
                },
                {
                    icon: <LineChart className="w-5 h-5" />,
                    label: "Trend Analysis",
                    value: "trends",
                    subtext: "Track changes over time"
                },
                {
                    icon: <Clock className="w-5 h-5" />,
                    label: "Daily Snapshots",
                    value: "daily",
                    subtext: "Regular updates on key numbers"
                }
            ]
        }
    ];

    const handleSelect = (field, value) => {
        setAssessment(prev => {
            if (Array.isArray(prev[field])) {
                const newValues = prev[field].includes(value)
                    ? prev[field].filter(v => v !== value)
                    : [...prev[field], value];
                return { ...prev, [field]: newValues };
            }
            return { ...prev, [field]: value };
        });
    };

    const getRecommendation = () => {
        const { dataSources, dataSize, processingNeeds } = assessment;

        if (dataSources.includes('legacy') || dataSize === 'enterprise') {
            return {
                title: "Enterprise Data Refinery",
                description: "Based on your vast data reserves, we recommend:",
                steps: [
                    "Establish a central data reservoir for all information",
                    "Create automated pipelines from all data sources",
                    "Implement real-time data refinement processes",
                    "Set up precision-engineered dashboards",
                    "Deploy secure distribution channels for all teams"
                ]
            };
        }

        if (dataSize === 'large' || processingNeeds.includes('trends')) {
            return {
                title: "Advanced Data Processing Plant",
                description: "To help refine your data efficiently:",
                steps: [
                    "Build a modern data collection pipeline",
                    "Install automated processing mechanisms",
                    "Create streamlined reporting flows",
                    "Set up trend monitoring systems",
                    "Enable collaborative data access"
                ]
            };
        }

        return {
            title: "Starter Data Solution",
            description: "Let's begin by organizing your information:",
            steps: [
                "Set up a structured way to store your data",
                "Create consistent formats for your information",
                "Build basic automated reports",
                "Establish regular backup procedures",
                "Train your team on the new system"
            ]
        };
    };

    return (
        <div className="min-h-screen pt-20 pb-8 px-4 bg-slate-900">
            <div className="max-w-4xl mx-auto bg-slate-800 rounded-lg p-8 text-slate-200">
                {step < questions.length ? (
                    <>
                        <div className="mb-8">
                            <div className="flex justify-between mb-4">
                                {questions.map((_, index) => (
                                    <div
                                        key={index}
                                        className={`h-2 flex-1 mx-1 rounded ${index <= step ? 'bg-blue-600' : 'bg-slate-600'
                                            }`}
                                    />
                                ))}
                            </div>
                            <h3 className="text-2xl font-bold mb-2">{questions[step].title}</h3>
                            <p className="text-slate-400">{questions[step].description}</p>
                        </div>

                        <div className="space-y-4">
                            {questions[step].options.map((option) => (
                                <button
                                    key={option.value}
                                    onClick={() => handleSelect(questions[step].field, option.value)}
                                    className={`w-full p-4 rounded-lg flex items-center justify-between
                                    ${assessment[questions[step].field]?.includes?.(option.value) ||
                                            assessment[questions[step].field] === option.value
                                            ? 'bg-blue-600'
                                            : 'bg-slate-700 hover:bg-slate-600'
                                        }`}
                                >
                                    <div className="flex items-center space-x-3">
                                        {option.icon && <div className="w-5 h-5 text-slate-400">{option.icon}</div>}
                                        <div className="text-left">
                                            <div className="font-medium">{option.label}</div>
                                            {option.subtext && (
                                                <div className="text-sm text-slate-400">{option.subtext}</div>
                                            )}
                                        </div>
                                    </div>
                                    {(assessment[questions[step].field]?.includes?.(option.value) ||
                                        assessment[questions[step].field] === option.value) && (
                                            <Check className="w-5 h-5 flex-shrink-0" />
                                        )}
                                </button>
                            ))}
                        </div>

                        <div className="mt-8 flex justify-end">
                            {step > 0 && (
                                <button
                                    onClick={() => setStep(step - 1)}
                                    className="mr-4 px-6 py-2 rounded-lg bg-slate-700 hover:bg-slate-600"
                                >
                                    Back
                                </button>
                            )}
                            <button
                                onClick={() => setStep(step + 1)}
                                className="px-6 py-2 rounded-lg bg-blue-600 hover:bg-blue-700 flex items-center space-x-2"
                            >
                                <span>Next</span>
                                <ArrowRight className="w-4 h-4" />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="text-center">
                        <h3 className="text-2xl font-bold mb-6">{getRecommendation().title}</h3>
                        <p className="text-slate-400 mb-8">{getRecommendation().description}</p>
                        <div className="space-y-4 mb-12">
                            {getRecommendation().steps.map((step, index) => (
                                <div
                                    key={index}
                                    className="bg-slate-700 p-4 rounded-lg flex items-center space-x-3"
                                >
                                    <div className="bg-blue-600 rounded-full p-2">
                                        <Check className="w-4 h-4" />
                                    </div>
                                    <span>{step}</span>
                                </div>
                            ))}
                        </div>
                        <button
                            onClick={() => onNavigate('/contact')}
                            className="mt-8 px-8 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 
                                     inline-flex items-center space-x-2 text-lg font-semibold"
                        >
                            <span>Contact Us</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssessmentWizard;